// src/components/layout/Sidebar.jsx

import { useAuth0 } from '@auth0/auth0-react';
import { LogOut, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Layout, Mic } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { authService } from '@/services/auth';
import { useVeradigmStore } from '@/stores/veradigmStore';
import { getNavigationItems, getComplianceNavigation, getUserMenuItems, getHelpNavItem } from '@/config/navigationConfig';
import { ClockInOutButton } from '../time-tracking/ClockInOutButton';
import { usePermissions } from '@/contexts/PermissionContext';
import { performLogout } from '@/utils/logoutCleanup';
import { confirmLogout, OFFLINE_UNAVAILABLE_MESSAGE } from '@/services/offline';
import { useOfflineNav } from '@/components/offline/useOfflineCapability';
import { useSupport } from '@/contexts/SupportContext';
import { useViewport } from '@/hooks/useViewport';

// Collapsed state persistence
const SIDEBAR_COLLAPSED_KEY = 'prism-sidebar-collapsed';

export function Sidebar({ isOpen, onClose }) {
  const { logout } = useAuth0();
  const location = useLocation();
  const { user } = useUser();
  const { solutions, mode, loading: featuresLoading, getRehabMode, canToggleScribeMode, toggleScribeMode } = useFeatureFlags();
  const isRehabPublicMode = solutions.rehab_prescribed && getRehabMode() === 'public';
  const { clearUserConnection } = useVeradigmStore();
  const { can, canAny, permissions: rolePermissions, effectiveIsAdmin } = usePermissions();
  const { openSupport } = useSupport();


  // Sidebar collapse preference (only for multi-feature mode). It only applies
  // to the static desktop sidebar (lg+): below that the sidebar is a drawer,
  // which has no expand toggle and whose hover flyouts are dead on touch, so it
  // always opens expanded.
  const [collapsedPref, setCollapsedPref] = useState(() => {
    if (mode === 'erx_mobile') return false; // Never collapse in eRx-only mode
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });
  const { isDesktop } = useViewport();
  const isCollapsed = collapsedPref && isDesktop;

  // Expanded sections state for sub-navigation
  const [expandedSections, setExpandedSections] = useState({});

  // Navigation counts
  const [pendingRenewalCount, setPendingRenewalCount] = useState(0);
  const [pendingChangeCount, setPendingChangeCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadFaxCount, setUnreadFaxCount] = useState(0);

  // Reset collapse state if switching to eRx-only mode
  useEffect(() => {
    if (mode === 'erx_mobile' && collapsedPref) {
      setCollapsedPref(false);
      localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
    }
  }, [mode, collapsedPref]);

  // Listen for collapse requests from the calendar provider panel
  useEffect(() => {
    const handleCollapseRequest = (e) => {
      if (mode === 'erx_mobile') return;
      const { collapsed } = e.detail || {};
      if (collapsed && !collapsedPref) {
        setCollapsedPref(true);
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'true');
      }
    };

    window.addEventListener('sidebar-collapse-request', handleCollapseRequest);
    return () => window.removeEventListener('sidebar-collapse-request', handleCollapseRequest);
  }, [mode, collapsedPref]);

  // Fetch counts for badges
  useEffect(() => {
    const fetchCounts = async () => {
      if (!user?.id) return;

      try {
        const promises = [];

        // Always fetch these for eRx
        if (solutions.erx_mobile) {
          promises.push(
            apiService.getPendingRenewalRequestCount(user.id),
            apiService.getPendingChangeRequestCount(user.id)
          );
        }

        // UPDATE THIS SECTION - Check for messaging using the same pattern as scheduling
        if (solutions.features?.messaging || solutions.messaging) {
          promises.push(apiService.getUnreadThreadsCount(user.id)); // Use getUnreadThreadsCount instead
        }

        if (solutions.features?.fax || solutions.fax) {
          promises.push(apiService.getUnreadFaxCount());
        }

        const results = await Promise.allSettled(promises);

        let idx = 0;

        if (solutions.erx_mobile) {
          if (results[idx]?.status === 'fulfilled') setPendingRenewalCount(results[idx].value);
          idx++;
          if (results[idx]?.status === 'fulfilled') setPendingChangeCount(results[idx].value);
          idx++;
        }

        if ((solutions.features?.messaging || solutions.messaging) && results[idx]?.status === 'fulfilled') {
          setUnreadMessages(results[idx].value);
          idx++;
        }

        if ((solutions.features?.fax || solutions.fax) && results[idx]?.status === 'fulfilled') {
          setUnreadFaxCount(results[idx].value);
          idx++;
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 15 * 60 * 1000); // Refresh every 15 minutes (badges are freshness hints; the fax count is a heavy backend call pre-v2)

    return () => clearInterval(interval);
  }, [user?.id, solutions]);

  // Toggle collapse state (only in multi-feature mode)
  const toggleCollapse = () => {
    if (mode === 'erx_mobile') return; // Don't allow collapse in eRx-only mode

    const newState = !collapsedPref;
    setCollapsedPref(newState);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newState));
  };

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Check if a route or its children are active
  const isRouteActive = (item) => {
    if (item.exact) {
      return location.pathname === item.href;
    }

    if (item.subNav) {
      return item.subNav.some(subItem => location.pathname.startsWith(subItem.href));
    }

    return location.pathname.startsWith(item.href);
  };

  // Handle logout
  const handleLogout = async () => {
    // Offline mode: drain the sync queue first (or confirm discarding
    // unsynced work). Must run before clearToken or the drain can't auth.
    // Resolves true immediately when offline mode is disabled.
    if (!(await confirmLogout())) return;
    authService.clearToken();
    clearUserConnection();
    apiService.clearVeradigmToken();
    await performLogout(logout);
  };

  // Get navigation items with counts
  const counts = {
    pendingRenewalCount,
    pendingChangeCount,
    unreadMessages,
    unreadFaxCount
  };

  // Offline mode: unavailable features render disabled (never hidden) while
  // the backend is unreachable. isNavDisabled is false for every item when
  // the flag is off or online.
  const { isNavDisabled } = useOfflineNav();

  const hiddenItems = (!effectiveIsAdmin && rolePermissions?.hidden_menu_items) || [];
  const promotedItems = rolePermissions?.promoted_menu_items || [];

  const navigationItems = getNavigationItems(solutions, mode, user, counts, { can, canAny, isAdmin: effectiveIsAdmin }, hiddenItems);
  const complianceItems = getComplianceNavigation(solutions, hiddenItems, { can, canAny });
  const userMenuItems = getUserMenuItems(user, mode === 'multi_feature', { can, canAny, isAdmin: effectiveIsAdmin }, hiddenItems, promotedItems);
  const helpItem = getHelpNavItem();

  // Render navigation item
  const renderNavItem = (item, depth = 0) => {
    const Icon = item.icon;
    const isActive = isRouteActive(item);
    const hasSubNav = item.subNav && item.subNav.length > 0;
    const isExpanded = expandedSections[item.name];

    // For collapsed sidebar with sections (multi-feature mode only)
    if (isCollapsed && hasSubNav && mode === 'multi_feature') {
      return (
        <div key={item.name} className="relative group">
          <Link
            to={item.href}
            onClick={onClose}
            className={cn(
              'relative flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-gray-600 hover:bg-gray-50'
            )}
          >
            {item.collapsedIcon ? (
              <span className="text-xl">{item.collapsedIcon}</span>
            ) : (
              <Icon className="h-5 w-5" />
            )}
            {/* Parent badge pinned to the icon corner so the count stays visible while collapsed */}
            {item.badge > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
                {item.badge}
              </span>
            )}
          </Link>

          {/* Hover tooltip showing section name and submenu */}
          <div className="absolute left-full ml-2 top-0 invisible group-hover:visible z-50">
            <div className="bg-white shadow-lg rounded-md border border-gray-200 py-1 min-w-[180px]">
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                {item.name}
              </div>
              {item.subNav.map(subItem => (
                subItem.href === '#support' ? (
                  <button
                    key={subItem.name}
                    onClick={() => { openSupport(); onClose(); }}
                    className="block w-full text-left px-3 py-1 text-sm text-gray-600 hover:bg-gray-50"
                  >
                    {subItem.name}
                  </button>
                ) : (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    onClick={onClose}
                    className={cn(
                      'block px-3 py-1 text-sm',
                      location.pathname === subItem.href
                        ? 'bg-primary/10 text-primary'
                        : 'text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    {subItem.name}
                    {subItem.badge && subItem.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                        {subItem.badge}
                      </span>
                    )}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Action item (e.g., Support opens slide-over instead of navigating)
    if (item.href === '#support') {
      return (
        <button
          key={item.name}
          onClick={() => { openSupport(); onClose(); }}
          className={cn(
            "w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50",
            depth > 0 && 'ml-6'
          )}
        >
          <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
          {item.name}
        </button>
      );
    }

    // Regular navigation item (eRx mode or non-expandable items)
    if (!hasSubNav || mode === 'erx_mobile') {
      const offlineDisabled = isNavDisabled(item.name);
      const isDisabled = (item.name === 'Patients' && isRehabPublicMode) || offlineDisabled;
      return (
        <Link
          key={item.name}
          title={offlineDisabled ? OFFLINE_UNAVAILABLE_MESSAGE : undefined}
          to={isDisabled ? '#' : item.href}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }
            onClose();
          }}
          className={cn(
            "group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md",
            depth > 0 && 'ml-6',
            isDisabled && "opacity-50 cursor-not-allowed",
            isActive && !isDisabled
              ? "bg-primary/10 text-primary"
              : isDisabled
                ? "text-gray-400"
                : "text-gray-600 hover:bg-gray-50"
          )}
        >
          <div className="flex items-center">
            {isCollapsed && mode === 'multi_feature' ? (
              item.collapsedIcon ? (
                <span className="text-xl">{item.collapsedIcon}</span>
              ) : (
                <Icon className="h-5 w-5" />
              )
            ) : (
              <>
                <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </>
            )}
          </div>
          {/* Only show badge if > 0 */}
          {!isCollapsed && item.badge && item.badge > 0 && (
            <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
              {item.badge}
            </span>
          )}
        </Link>
      );
    }

    // Expandable section (multi-feature mode only)
    return (
      <div key={item.name}>
        <button
          onClick={() => toggleSection(item.name)}
          className={cn(
            'w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md',
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-gray-600 hover:bg-gray-50'
          )}
        >
          <div className="flex items-center">
            <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
            {item.name}
          </div>
          {/* Parent badge so the count is visible before the section is opened */}
          {item.badge > 0 && (
            <span className="ml-auto mr-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
              {item.badge}
            </span>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {isExpanded && (
          <div className="mt-1 space-y-1">
            {item.subNav.map(subItem => renderNavItem(subItem, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Determine app title based on mode
  const getAppTitle = () => {
    if (featuresLoading) return 'Loading...';
    if (mode === 'multi_feature') {
      // For multi-feature mode, show logo and PrismEHR text
      return (
        <div className="flex items-center">
          <img
            src="/prism-logo.png"
            alt="PrismEHR"
            className="h-8 w-8 mr-2"
          />
          <span className="text-xl font-bold">PrismEHR</span>
        </div>
      );
    }
    if (mode === 'form_builder') return 'CuratoForm';
    if (mode === 'ready2exercise') return 'Ready2Exercise';
    if (mode === 'rehab_prescribed') return 'Rehab Prescribed';
    if (mode === 'erx_mobile') return 'eRx Mobile';
    return 'Loading...';
  };

  return (
    <>
      {/* Backdrop for mobile. z-[45]: above the pages' sticky chrome (toolbars
          z-40, calendar panel toggle z-[42]) so they dim too, below the drawer (z-50) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 lg:hidden z-[45]"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          // z-50/z-40 (drawer/backdrop) like the Help/Support slide-overs: the phone
          // calendar's sticky header is z-40 and would otherwise paint over the
          // drawer's top items. Desktop is lg:static, where z-index is moot.
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white shadow-lg transition-all duration-200 ease-in-out lg:static lg:translate-x-0',
          isCollapsed && mode === 'multi_feature' ? 'w-16' : 'w-64',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className={cn(
          'flex h-16 flex-shrink-0 items-center px-4',
          isCollapsed && mode === 'multi_feature' && 'justify-center'
        )}>
          {/* Show different content based on mode and collapsed state */}
          {mode === 'multi_feature' ? (
            isCollapsed ? (
              // Show just the logo when collapsed
              <img
                src="/prism-logo.png"
                alt="PrismEHR"
                className="h-8 w-8"
              />
            ) : (
              // Show logo and text when expanded
              <>
                <img
                  src="/prism-logo.png"
                  alt="PrismEHR"
                  className="h-8 w-8 flex-shrink-0"
                />
                <span className="ml-2 text-xl font-bold">PrismEHR</span>
              </>
            )
          ) : (
            // For other modes, show the default layout icon and title
            <>
              <Layout className="h-8 w-8 text-primary flex-shrink-0" />
              <span className="ml-2 text-xl font-bold">{getAppTitle()}</span>
            </>
          )}
        </div>

        {/* Clock In/Out — pinned below header */}
        <div className={cn(
          'flex-shrink-0 py-2 hidden lg:block border-b border-gray-100',
          isCollapsed && mode === 'multi_feature' ? 'px-2 flex justify-center' : 'px-4'
        )}>
          <ClockInOutButton compact={isCollapsed && mode === 'multi_feature'} />
        </div>

        {/* Collapse toggle for desktop (only in multi-feature mode) */}
        {mode === 'multi_feature' && (
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex absolute -right-3 top-32 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:shadow-lg transition-shadow"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            )}
          </button>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto">
          <div className="space-y-1 px-2 pb-4">
            {/* Show loading state */}
            {featuresLoading ? (
              <div className="px-2 py-4 text-sm text-gray-500">
                Loading features...
              </div>
            ) : (
              <>
                {/* Main navigation */}
                {navigationItems.map(item => renderNavItem(item))}

                {/* Compliance section for eRx */}
                {complianceItems.length > 0 && (
                  <>
                    <div className="py-2">
                      <div className="border-t border-gray-200" />
                      {(!isCollapsed || mode !== 'multi_feature') && (
                        <div className="mt-3 px-2">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            DEA Compliance
                          </p>
                        </div>
                      )}
                    </div>
                    {complianceItems.map(item => renderNavItem(item))}
                  </>
                )}

                {/* User section */}
                <div className="py-2">
                  <div className="border-t border-gray-200" />
                </div>

                {userMenuItems.map(item => renderNavItem(item))}

                {/* Switch to Scribe mode */}
                {canToggleScribeMode && mode !== 'prism_scribe' && (
                  <button
                    onClick={toggleScribeMode}
                    className={cn(
                      'w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50',
                      isCollapsed && mode === 'multi_feature' && 'justify-center'
                    )}
                  >
                    <Mic className={cn(
                      'h-5 w-5 flex-shrink-0',
                      (!isCollapsed || mode !== 'multi_feature') && 'mr-3'
                    )} />
                    {(!isCollapsed || mode !== 'multi_feature') && 'Switch to Scribe'}
                  </button>
                )}

                {/* Help & Support */}
                {renderNavItem(helpItem)}

                {/* Logout button */}
                <button
                  onClick={handleLogout}
                  className={cn(
                    'w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50',
                    isCollapsed && mode === 'multi_feature' && 'justify-center'
                  )}
                >
                  <LogOut className={cn(
                    'h-5 w-5 flex-shrink-0',
                    (!isCollapsed || mode !== 'multi_feature') && 'mr-3'
                  )} />
                  {(!isCollapsed || mode !== 'multi_feature') && 'Sign Out'}
                </button>
              </>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}